import type { Membership } from '@wix/ambassador-ecom-memberships-spi-host-v1-membership/types';
import { BalanceType } from '@wix/ambassador-ecom-memberships-spi-host-v1-membership/types';
import { BenefitType } from '@wix/ambassador-pricing-plans-v2-benefit/types';
import type { DateTimeFormatter } from '@wix/bookings-date-time';
import type { Experiments } from '@wix/yoshi-flow-editor';
import { ExperimentsConsts } from '../../../consts/experiments';
import {
  PaymentMethod,
  type PaymentOption,
  PaymentType,
  type PlanInfoWithDescription,
  type PlanWithDescription,
  ReservedPaymentOptionIds,
  ReservedPaymentOptionLabels,
  type TFunction,
} from '../../../types/types';
import {
  OfferedAsType,
  ServicePayment,
} from '../../mappers/serviceMapper/utils/payment.mapper';

export const isOfferedAsOneTimeOnly = (servicePayment: ServicePayment) =>
  servicePayment.offeredAs.length === 1 && isOfferedAsOneTime(servicePayment);

export const isOfferedAsOneTime = (servicePayment: ServicePayment) =>
  servicePayment.offeredAs.includes(OfferedAsType.ONE_TIME);

export const isOfferedAsPricingPlanOnly = (servicePayment: ServicePayment) =>
  servicePayment.offeredAs.length <= 1 &&
  isServiceHavePricingPlans(servicePayment);

export const getBenefitType = (membership: Membership) =>
  membership.credits ? BenefitType.LIMITED : BenefitType.UNLIMITED;

export const isServiceHavePricingPlans = (servicePayment: ServicePayment) =>
  servicePayment.pricingPlanInfo?.pricingPlans?.length! > 0;

export const isOfferedAsPricingPlanOnlyWithoutPlansConnected = (
  servicePayment: ServicePayment,
) =>
  servicePayment.offeredAs.length === 1 &&
  servicePayment.offeredAs[0] === OfferedAsType.PRICING_PLAN &&
  !isServiceHavePricingPlans(servicePayment);

export const createBuyPlanOption = (
  plan: PlanWithDescription,
): PaymentOption => ({
  id: plan.id!,
  label: plan.name!,
  disabled: false,
  type: PaymentType.BUY_PLAN,
  pricingDetails: plan.description,
});

export const createBuyPlanBenefitOption = (
  planInfo: PlanInfoWithDescription,
): PaymentOption => ({
  id: planInfo?.plan?.id!,
  label: planInfo?.plan?.name!,
  suffix: planInfo.suffix,
  disabled: false,
  type: PaymentType.BUY_PLAN,
  pricingDetails: planInfo.description,
});

export const createPayOfflineOption = (
  price: string,
  t: TFunction,
  experiments: Experiments,
  discountRuleName?: string,
  isCart?: boolean,
): PaymentOption => {
  const isUseNewBenefitsAPIUoUEnabled =
    experiments.enabled(ExperimentsConsts.UseNewBenefitsAPIUoU) ?? false;
  const isAutomaticDiscountsEnabled = experiments.enabled(
    ExperimentsConsts.AutomaticDiscountsUoU,
  );

  const payOfflineOption: PaymentOption = {
    id: ReservedPaymentOptionIds.SingleSession,
    label: ReservedPaymentOptionLabels.OFFLINE,
    disabled: false,
    type: PaymentType.SINGLE_SESSION,
    suffix: isUseNewBenefitsAPIUoUEnabled ? price : undefined,
    pricingDetails: isUseNewBenefitsAPIUoUEnabled
      ? t('app.bookings-form.payment-section.service-dropdown.pay-offline')
      : t(
          'app.bookings-form.payment-section.service-dropdown.pay-offline-subtitle',
          { price },
        ),
    discountDetails: isAutomaticDiscountsEnabled
      ? {
          name: discountRuleName,
          isCart,
          showAtSuffix: !isUseNewBenefitsAPIUoUEnabled,
        }
      : undefined,
  };

  return payOfflineOption;
};

export const createPayOnlineOption = (
  price: string,
  t: TFunction,
  experiments: Experiments,
  discountRuleName?: string,
  isCart?: boolean,
): PaymentOption => {
  const isUseNewBenefitsAPIUoUEnabled = experiments.enabled(
    ExperimentsConsts.UseNewBenefitsAPIUoU,
  );
  const isAutomaticDiscountsEnabled = experiments.enabled(
    ExperimentsConsts.AutomaticDiscountsUoU,
  );

  const payOnlineOption: PaymentOption = {
    id: ReservedPaymentOptionIds.SingleSession,
    label: ReservedPaymentOptionLabels.ONLINE,
    disabled: false,
    type: PaymentType.SINGLE_SESSION,
    suffix: isUseNewBenefitsAPIUoUEnabled ? price : undefined,
    pricingDetails: isUseNewBenefitsAPIUoUEnabled
      ? t('app.bookings-form.payment-section.service-dropdown.pay-online')
      : t(
          'app.bookings-form.payment-section.service-dropdown.pay-online-subtitle',
          { price },
        ),
    discountDetails: isAutomaticDiscountsEnabled
      ? {
          name: discountRuleName,
          isCart,
          showAtSuffix: !isUseNewBenefitsAPIUoUEnabled,
        }
      : undefined,
  };

  return payOnlineOption;
};

export const createFreeOption = (): PaymentOption => ({
  id: ReservedPaymentOptionIds.SingleSession,
  label: ReservedPaymentOptionLabels.FREE,
  disabled: false,
  type: PaymentType.SINGLE_SESSION,
});

export const createCustomPriceOption = (
  customPrice: string,
): PaymentOption => ({
  id: ReservedPaymentOptionIds.SingleSession,
  label: customPrice,
  disabled: false,
  type: PaymentType.SINGLE_SESSION,
});

export function createUsePlanOption(
  membership: Membership,
  numberOfParticipants: number,
  t: TFunction,
  dateAndTimeFormatter: DateTimeFormatter,
  disabledReason: string | undefined,
  experiments: Experiments,
): PaymentOption {
  const disabled =
    Boolean(disabledReason) ||
    isDisabledMembership({
      membership,
      numberOfParticipants,
    });
  const isUseNewBenefitsAPIUoUEnabled =
    experiments.enabled(ExperimentsConsts.UseNewBenefitsAPIUoU) ?? false;

  if (isUseNewBenefitsAPIUoUEnabled) {
    const isSessionBased =
      membership?.credits?.balanceType === BalanceType.PUNCH_CARD;

    const expirationDate = membership.expirationDate
      ? t('app.payment.expires.text', {
          expirationDate: dateAndTimeFormatter.formatDate(
            membership.expirationDate.toISOString(),
          ),
        })
      : undefined;

    const { remaining, total, redemptionCost } = membership.credits || {};
    const planUsage = total
      ? t(
          isSessionBased
            ? 'app.payment.plan-usage.sessions-left'
            : 'app.payment.plan-usage.credits-left',
          {
            remaining,
            total,
          },
        )
      : undefined;

    const pricingDetails =
      planUsage && expirationDate
        ? t('app.payment.plan-usage.details', {
            planUsage,
            expirationDate,
          })
        : expirationDate ?? planUsage;

    const suffix =
      redemptionCost && !isSessionBased
        ? t('app.payment.plan-usage.credits-cost', { redemptionCost })
        : undefined;

    return {
      id: membership.id!,
      label: membership.name?.original!,
      benefitInfo: { benefit: { benefitType: getBenefitType(membership) } },
      disabled,
      disabledReason,
      suffix,
      pricingDetails,
      creditRemain: remaining || 0,
      creditCost: redemptionCost || 1,
      type: PaymentType.USE_PLAN,
    };
  }

  const validUntil = membership.expirationDate
    ? t('app.payment.valid-until.text', {
        validUntil: dateAndTimeFormatter.formatDate(
          membership.expirationDate.toISOString(),
        ),
      })
    : undefined;
  const creditRemain = membership?.credits?.remaining || undefined;
  const planUsage =
    membership?.credits?.remaining && membership?.credits?.total
      ? `${membership.credits.remaining}/${membership.credits.total}`
      : undefined;
  const pricingDetails = [validUntil, planUsage].filter((x) => x).join(', ');

  return {
    id: membership.id!,
    label: membership.name?.original!,
    benefitInfo: { benefit: { benefitType: getBenefitType(membership) } },
    disabled,
    disabledReason,
    creditRemain,
    ...(pricingDetails ? { pricingDetails } : {}),
    ...(planUsage ? { suffix: planUsage } : {}),
    ...(validUntil ? { validUntil } : {}),
    type: PaymentType.USE_PLAN,
  };
}

export const isDisabledMembership = ({
  membership,
  numberOfParticipants,
}: {
  membership: Membership;
  numberOfParticipants: number;
}) => {
  const isUnlimitedMembership = !membership?.credits?.remaining;
  return (
    Number(membership?.credits?.remaining) < numberOfParticipants ||
    (isUnlimitedMembership && numberOfParticipants > 1)
  );
};

export const enum DepositOrFullAmountOptionIds {
  Deposit = 'DEPOSIT_OPTION',
  FullAmount = 'FULL_AMOUNT_OPTION',
}

export const isDepositOrFullAmountOption = (option: PaymentOption): boolean =>
  option.id === DepositOrFullAmountOptionIds.Deposit ||
  option.id === DepositOrFullAmountOptionIds.FullAmount;

export const isOnlineSingleSessionOption = (option: PaymentOption): boolean =>
  option.type === PaymentType.SINGLE_SESSION &&
  (option.label === ReservedPaymentOptionLabels.ONLINE ||
    isDepositOrFullAmountOption(option));

export const getPaymentTypeFromOption = (
  paymentOption: PaymentOption,
): PaymentMethod => {
  if (paymentOption.type === PaymentType.USE_PLAN) {
    return PaymentMethod.MEMBERSHIP;
  } else if (paymentOption.type === PaymentType.BUY_PLAN) {
    return PaymentMethod.MEMBERSHIP;
  } else if (isOnlineSingleSessionOption(paymentOption)) {
    return PaymentMethod.ONLINE;
  }
  return PaymentMethod.OFFLINE;
};

export const isDepositOrFullAmountFeatureEnabled = ({
  experiments,
  fullUpfrontPaymentAllowed,
  deposit,
  paymentTypes,
}: {
  experiments?: Experiments;
  fullUpfrontPaymentAllowed?: boolean;
  deposit: number;
  paymentTypes: PaymentMethod[];
}): boolean =>
  experiments?.enabled(ExperimentsConsts.DepositeOrFullAmountUoU) === true &&
  fullUpfrontPaymentAllowed === true &&
  deposit > 0 &&
  paymentTypes.includes(PaymentMethod.ONLINE);

export const DepositOrFullAmountTranslationKeys = {
  depositLabel:
    'app.bookings-form.payment-section.service-dropdown.pay-deposit',
  depositSubtitle:
    'app.bookings-form.payment-section.service-dropdown.pay-deposit-subtitle',
  fullAmountLabel:
    'app.bookings-form.payment-section.service-dropdown.pay-full-amount',
  fullAmountSubtitle:
    'app.bookings-form.payment-section.service-dropdown.pay-full-amount-subtitle',
} as const;

export const createDepositOption = (
  depositPrice: string,
  t: TFunction,
): PaymentOption => {
  return {
    id: DepositOrFullAmountOptionIds.Deposit,
    label: t(DepositOrFullAmountTranslationKeys.depositLabel),
    disabled: false,
    type: PaymentType.SINGLE_SESSION,
    pricingDetails: t(DepositOrFullAmountTranslationKeys.depositSubtitle, {
      price: depositPrice,
    }),
  };
};

export const createFullAmountOption = (
  fullPrice: string,
  t: TFunction,
): PaymentOption => {
  return {
    id: DepositOrFullAmountOptionIds.FullAmount,
    label: t(DepositOrFullAmountTranslationKeys.fullAmountLabel),
    disabled: false,
    type: PaymentType.SINGLE_SESSION,
    pricingDetails: t(DepositOrFullAmountTranslationKeys.fullAmountSubtitle, {
      price: fullPrice,
    }),
  };
};
